import { useCallback, useRef } from 'react';
import type { ForwardedRef, RefObject } from 'react';

type UseNodeRefAndForwardItResult<T> = readonly [
  /** you should pass this to the `ref` prop in JSX */
  callbackRef: (node: T) => void,
  /** you can freely use this inside your component */
  ref: RefObject<T>
];

/**
 * Similar to `useRef`, but helpful when using `React.ForwardedRef`.
 *
 * It takes a `forwardedRef` (to forward the ref to), and returns a `callbackRef`
 * (to pass to the `ref` prop in JSX), and a `ref` (to freely use inside your component)
 * @param forwardedRef will be forwarded to this
 * @returns [callbackRef, ref]
 */
export function useNodeRefAndForwardIt<T>(
  forwardedRef: ForwardedRef<T>
): UseNodeRefAndForwardItResult<T> {
  const ref = useRef<T | null>(null);
  const callbackRef = useCallback(
    (node: T) => {
      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else {
          forwardedRef.current = node;
        }
      }
      ref.current = node;
    },
    [forwardedRef]
  );
  return [callbackRef, ref as RefObject<T>] as const;
}
